<template>
  <div>
    <PageHeader title="จัดการผู้ใช้งาน"></PageHeader>
    <b-container fluid="xl">
      <div class="py-5">
        <div class="inner bg-container">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h6 class="mb-0"><strong>รายชื่อผู้ใช้งาน</strong></h6>
            <b-button variant="primary" @click="openAddModal()">
              + เพิ่มผู้ใช้งาน
            </b-button>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-3">
            <span>มีทั้งหมด {{ totalRows }} คน</span>
            <b-input-group style="width: 250px">
              <b-input-group-prepend>
                <span class="input-group-text">
                  <b-icon-search></b-icon-search>
                </span>
              </b-input-group-prepend>
              <b-form-input
                v-model="filter"
                type="search"
                placeholder="พิมพ์ชื่อเพื่อค้นหา"
              ></b-form-input>
            </b-input-group>
          </div>

          <b-table
            id="wannasorn-role-table"
            :filter="filter"
            @filtered="onFiltered"
            :per-page="perPage"
            :current-page="currentPage"
            v-if="tableData != null"
            striped
            hover
            :items="tableData"
            :fields="fields"
            table-class="pre-formatted"
          >
            <template v-slot:cell(subCompany)="{ item }">
              {{ item.subCompanyName || "ทั้งหมด" }}
            </template>

            <template v-slot:cell(roles)="{ item }">
              <span
                v-for="role in item.parsedRoles"
                :key="role.role"
                class="role-badge mr-1 mb-1"
                :class="getRoleBadgeClass(role.name)"
              >
                {{ role.name }}
              </span>
            </template>

            <template v-slot:cell(actions)="{ item }">
              <b-button
                variant="outline-primary"
                size="sm"
                class="mr-1"
                @click="openEditModal(item)"
              >
                <b-icon-pencil></b-icon-pencil>
              </b-button>
              <b-button
                variant="outline-danger"
                size="sm"
                @click="openDeleteModal(item)"
              >
                <b-icon-trash></b-icon-trash>
              </b-button>
            </template>
          </b-table>
          <b-table v-else :fields="fields"></b-table>

          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="mr-2">จำนวนแถวต่อหน้า</span>
              <b-form-select
                v-model="perPage"
                :options="pageOptions"
                style="width: 100px"
              ></b-form-select>
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              aria-controls="wannasorn-role-table"
            ></b-pagination>
          </div>

          <!-- Add Modal -->
          <b-modal
            ref="addModal"
            title="เพิ่มสถาบันและหน้าที่ให้กับผู้ใช้งาน"
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
            size="lg"
          >
            <div class="mb-3">
              <h6><strong>ชื่อผู้ใช้งานในระบบ</strong></h6>
              <v-select
                :options="userSearchResults"
                label="name"
                v-model="addForm.member"
                @search="onUserSearch"
                @input="onMemberSelected()"
                :filterable="false"
                placeholder="กรุณาพิมพ์เพื่อค้นหาผู้ใช้งาน..."
              >
                <template #no-options="{ search }">
                  <span v-if="search.length === 0">กรุณาพิมพ์เพื่อค้นหา</span>
                  <span v-else>ไม่พบผู้ใช้งาน</span>
                </template>
              </v-select>
            </div>
            <div class="mb-3">
              <h6><strong>สถาบัน</strong></h6>
              <v-select
                :options="addSubCompanyOptions"
                label="scName"
                v-model="addForm.subCompany"
                :selectable="(option) => !option.$isDisabled"
                placeholder="กรุณาพิมพ์เพื่อค้นหาสถาบัน..."
                @input="onAddSubCompanyChange"
              >
                <template #option="option">
                  <span :class="{ 'text-muted': option.$isDisabled }">
                    {{ option.scName }}
                    <small v-if="option.$isDisabled" class="text-muted">
                      (ไม่สามารถเลือกสถาบันที่เคยเพิ่มแล้วได้)
                    </small>
                  </span>
                </template>
              </v-select>
            </div>
            <div class="mb-3">
              <h6><strong>เลือกหน้าที่</strong></h6>
              <v-select
                v-model="addForm.roles"
                :options="roleList"
                label="name"
                multiple
                placeholder="-- เลือกหน้าที่ให้ผู้ใช้งาน --"
              ></v-select>
            </div>
            <template #modal-footer>
              <b-button variant="secondary" @click="$refs.addModal.hide()">
                ยกเลิก
              </b-button>
              <b-button
                variant="primary"
                :disabled="!canSubmitAdd || isLoading"
                @click="submitAdd()"
              >
                ยืนยันการเพิ่มผู้ใช้งาน
              </b-button>
              <b-spinner
                variant="success"
                label="Spinning"
                v-show="isLoading"
              ></b-spinner>
            </template>
          </b-modal>

          <!-- Edit Modal -->
          <b-modal
            ref="editModal"
            title="แก้ไขหน้าที่ให้กับผู้ใช้งาน"
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
            size="lg"
          >
            <div class="mb-3">
              <h6><strong>ชื่อผู้ใช้งานในระบบ</strong></h6>
              <p>{{ editForm.name }} ({{ editForm.email || "ไม่มีอีเมล" }})</p>
            </div>
            <div class="mb-3">
              <h6><strong>สถาบัน</strong></h6>
              <p>{{ editForm.subCompanyName || "ทั้งหมด" }}</p>
            </div>
            <div class="mb-3">
              <h6><strong>เลือกหน้าที่</strong></h6>
              <v-select
                v-model="editForm.roles"
                :options="roleList"
                label="name"
                multiple
                placeholder="-- เลือกหน้าที่ให้ผู้ใช้งาน --"
              ></v-select>
            </div>
            <template #modal-footer>
              <b-button variant="secondary" @click="$refs.editModal.hide()">
                ยกเลิก
              </b-button>
              <b-button
                variant="primary"
                :disabled="editForm.roles.length === 0 || isLoading"
                @click="submitEdit()"
              >
                บันทึกหน้าที่
              </b-button>
              <b-spinner
                variant="success"
                label="Spinning"
                v-show="isLoading"
              ></b-spinner>
            </template>
          </b-modal>

          <!-- Delete Modal -->
          <b-modal
            ref="deleteModal"
            title="ลบผู้ใช้งาน"
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
          >
            <p>
              ต้องการลบ
              <strong>{{ deleteForm.name }}</strong>
              ({{ deleteForm.email || "ไม่มีอีเมล" }})
              ออกจากสถาบัน
              <strong>{{ deleteForm.subCompanyName || "ทั้งหมด" }}</strong>
              ใช่ หรือ ไม่?
            </p>
            <template #modal-footer>
              <b-button variant="secondary" @click="$refs.deleteModal.hide()">
                ยกเลิก
              </b-button>
              <b-button
                variant="danger"
                :disabled="isLoading"
                @click="submitDelete()"
              >
                ใช่
              </b-button>
              <b-spinner
                variant="success"
                label="Spinning"
                v-show="isLoading"
              ></b-spinner>
            </template>
          </b-modal>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import PageHeader from "../../layouts/PageHeader";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import api from "@/services/api.js";
import AuthMixin from "@/mixins/auth-mixin.js";
import _ from "lodash";

export default {
  components: { PageHeader, vSelect },
  mixins: [AuthMixin],

  data() {
    return {
      totalRows: 0,
      filter: null,
      pageOptions: [10, 20, 30],
      perPage: 10,
      currentPage: 1,
      fields: [
        { key: "name", label: "ชื่อ-นามสกุล" },
        { key: "email", label: "อีเมล" },
        { key: "username", label: "ชื่อผู้ใช้งาน" },
        { key: "subCompany", label: "สถาบัน" },
        { key: "roles", label: "หน้าที่" },
        { key: "actions", label: "จัดการ", class: "text-center" },
      ],
      isLoading: false,
      comId: this.$store.state.Company.companyInfo.id,
      comType: null,
      roleList: [],
      orgRoles: [],
      subCompanyRoles: [],
      allRolesMap: {},
      tableData: null,
      allUsers: [],
      userSearchResults: [],
      subCompanyList: [],
      memberSubCompanies: [],
      addForm: {
        member: null,
        subCompany: null,
        roles: [],
      },
      editForm: {
        memberId: null,
        name: "",
        email: "",
        subCompanyId: null,
        subCompanyName: "",
        roles: [],
      },
      deleteForm: {
        memberId: null,
        name: "",
        email: "",
        subCompanyId: null,
        subCompanyName: "",
      },
    };
  },

  computed: {
    canSubmitAdd() {
      return (
        this.addForm.member != null &&
        this.addForm.subCompany != null &&
        this.addForm.roles.length > 0
      );
    },
    addSubCompanyOptions() {
      var allOption = { scId: null, scName: "ทั้งหมด" };
      var hasNull = false;
      for (var j = 0; j < this.memberSubCompanies.length; j++) {
        if (this.memberSubCompanies[j].subCompanyId === null) {
          hasNull = true;
          break;
        }
      }
      var options = [Object.assign({}, allOption, { $isDisabled: hasNull })];
      for (var i = 0; i < this.subCompanyList.length; i++) {
        var sc = this.subCompanyList[i];
        var isAssigned = false;
        for (var k = 0; k < this.memberSubCompanies.length; k++) {
          if (this.memberSubCompanies[k].subCompanyId === sc.scId) {
            isAssigned = true;
            break;
          }
        }
        options.push(Object.assign({}, sc, { $isDisabled: isAssigned }));
      }
      return options;
    },
  },

  created() {
    this.onUserSearch = _.debounce(this._searchUsers, 300);
  },

  async mounted() {
    this.comType = await api.get("v1/companyType/" + this.comId);
    this.comType = this.comType.data["com_type"];

    if (this.isWannasorn) {
      var rolesRes = await api.get("/v1/wannasorn-roles");
      this.orgRoles = rolesRes.data.org;
      this.subCompanyRoles = rolesRes.data.subCompany;
      var map = {};
      var i;
      for (i = 0; i < this.orgRoles.length; i++) {
        map[this.orgRoles[i].role] = this.orgRoles[i];
      }
      for (i = 0; i < this.subCompanyRoles.length; i++) {
        map[this.subCompanyRoles[i].role] = this.subCompanyRoles[i];
      }
      this.allRolesMap = map;
    } else if (this.comType == "TRAINER" || this.comType == "WHITELABEL_TUTOR") {
      this.roleList = [
        { name: "ผู้ดูแลระบบ", role: 9 },
        { name: "ผู้สอน", role: 10 },
      ];
    } else {
      this.roleList = [
        { name: "ผู้ดูแลระบบ", role: 3 },
        { name: "ผู้สอน", role: 4 },
      ];
    }

    await this.loadTableData();
    await this.loadUserList();

    try {
      var scRes = await api.get(
        "/v1/companies/" + this.comId + "/sub-companies"
      );
      this.subCompanyList = scRes.data.data || scRes.data;
    } catch (e) {
      this.subCompanyList = [];
    }
  },

  methods: {
    loadTableData: function () {
      var self = this;
      return api
        .get("/v1/companies/" + this.comId + "/member-has-role/" + this.comType)
        .then(function (hasRole) {
          self.tableData = hasRole.data.map(function (row) {
            var arr = row.role ? row.role.split(",") : [];
            var parsedRoles = arr.map(function (r) {
              var roleId = parseInt(r, 10);
              var found = self.isWannasorn
                ? self.allRolesMap[roleId]
                : self.roleList.find(function (rl) {
                    return rl.role === roleId;
                  });
              return found || { name: "Role " + roleId, role: roleId };
            });
            return Object.assign({}, row, { parsedRoles: parsedRoles });
          });
          self.totalRows = self.tableData.length;
        });
    },

    loadUserList: function () {
      var self = this;
      return Promise.all([
        api.get("/v1/companies/" + this.comId + "/member-has-role/" + this.comType),
        api.get("/v1/companies/" + this.comId + "/member-without-role/" + this.comType),
      ]).then(function (results) {
        var hasRoleUsers = results[0].data || [];
        var withoutRoleUsers = results[1].data || [];
        var seen = {};
        var merged = [];
        var i;
        for (i = 0; i < hasRoleUsers.length; i++) {
          var id = hasRoleUsers[i].memberId;
          if (!seen[id]) {
            seen[id] = true;
            merged.push(hasRoleUsers[i]);
          }
        }
        for (i = 0; i < withoutRoleUsers.length; i++) {
          var id2 = withoutRoleUsers[i].memberId;
          if (!seen[id2]) {
            seen[id2] = true;
            merged.push(withoutRoleUsers[i]);
          }
        }
        self.allUsers = merged;
      });
    },

    onFiltered: function (filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    _searchUsers: function (search) {
      if (!search || search.length < 1) {
        this.userSearchResults = [];
        return;
      }
      var term = search.toLowerCase();
      var results = [];
      for (var i = 0; i < this.allUsers.length; i++) {
        if (results.length >= 10) break;
        var user = this.allUsers[i];
        var name = (user.name || "").toLowerCase();
        var email = (user.email || "").toLowerCase();
        var username = (user.username || "").toLowerCase();
        if (
          name.indexOf(term) > -1 ||
          email.indexOf(term) > -1 ||
          username.indexOf(term) > -1
        ) {
          results.push(user);
        }
      }
      this.userSearchResults = results;
    },

    getRoleBadgeClass: function (roleName) {
      var map = {
        "ผู้ดูแลระบบ": "badge-admin",
        "ผู้สอน": "badge-instructor",
        "ผู้จัดการคอร์ส": "badge-course-manager",
        "เจ้าหน้าที่ส่งหนังสือ": "badge-book-delivery",
        "เจ้าหน้าที่บัญชี": "badge-accounting",
        "เจ้าหน้าที่ทั่วไป": "badge-general",
      };
      return map[roleName] || "badge-default";
    },

    openAddModal: function () {
      this.addForm = { member: null, subCompany: null, roles: [] };
      this.memberSubCompanies = [];
      this.userSearchResults = [];
      if (this.isWannasorn) {
        this.roleList = [];
      }
      this.$refs.addModal.show();
    },

    onAddSubCompanyChange: function () {
      this.addForm.roles = [];
      if (!this.addForm.subCompany) {
        this.roleList = [];
        return;
      }
      if (this.isWannasorn) {
        if (this.addForm.subCompany.scId === null) {
          this.roleList = this.orgRoles;
        } else {
          this.roleList = this.subCompanyRoles;
        }
      }
    },

    onMemberSelected: function () {
      if (!this.addForm.member) {
        this.memberSubCompanies = [];
        return;
      }
      var self = this;
      api
        .get(
          "/v1/companies/" +
            this.comId +
            "/members/" +
            this.addForm.member.memberId +
            "/sub-companies"
        )
        .then(function (res) {
          self.memberSubCompanies = res.data;
        })
        .catch(function () {
          self.memberSubCompanies = [];
        });
    },

    submitAdd: function () {
      this.isLoading = true;
      var self = this;
      api
        .post(
          "/v1/companies/" +
            this.comId +
            "/members/" +
            this.addForm.member.memberId +
            "/sub-companies",
          {
            scId: this.addForm.subCompany.scId,
            roles: this.addForm.roles,
          }
        )
        .then(function () {
          self.$refs.addModal.hide();
          self.isLoading = false;
          self.loadTableData();
          self.loadUserList();
        })
        .catch(function () {
          self.isLoading = false;
        });
    },

    openEditModal: function (item) {
      if (this.isWannasorn) {
        this.roleList = item.subCompanyId === null
          ? this.orgRoles
          : this.subCompanyRoles;
      }
      this.editForm = {
        memberId: item.memberId,
        name: item.name,
        email: item.email,
        subCompanyId: item.subCompanyId,
        subCompanyName: item.subCompanyName,
        roles: [].concat(item.parsedRoles),
      };
      this.$refs.editModal.show();
    },

    submitEdit: function () {
      this.isLoading = true;
      var self = this;
      api
        .put(
          "/v1/companies/" +
            this.comId +
            "/members/" +
            this.editForm.memberId +
            "/sub-companies/" +
            (this.editForm.subCompanyId || "all"),
          {
            roles: this.editForm.roles,
          }
        )
        .then(function () {
          self.$refs.editModal.hide();
          self.isLoading = false;
          self.loadTableData();
          self.loadUserList();
        })
        .catch(function () {
          self.isLoading = false;
        });
    },

    openDeleteModal: function (item) {
      this.deleteForm = {
        memberId: item.memberId,
        name: item.name,
        email: item.email,
        subCompanyId: item.subCompanyId,
        subCompanyName: item.subCompanyName,
      };
      this.$refs.deleteModal.show();
    },

    submitDelete: function () {
      this.isLoading = true;
      var self = this;
      api
        .delete(
          "/v1/companies/" +
            this.comId +
            "/members/" +
            this.deleteForm.memberId +
            "/sub-companies/" +
            (this.deleteForm.subCompanyId || "all")
        )
        .then(function () {
          self.$refs.deleteModal.hide();
          self.isLoading = false;
          self.loadTableData();
          self.loadUserList();
        })
        .catch(function () {
          self.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.role-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  color: #fff;
}

.badge-admin {
  background-color: #28a745;
}

.badge-instructor {
  background-color: #007bff;
}

.badge-course-manager {
  background-color: #003f88;
}

.badge-book-delivery {
  background-color: #17a2b8;
}

.badge-accounting {
  background-color: #dc3545;
}

.badge-general {
  background-color: #ffc107;
  color: #333;
}

.badge-default {
  background-color: #6c757d;
}

.pre-formatted {
  white-space: pre;
}
</style>
